/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import _ from 'lodash';
import { createForm } from '../../helpers/form';

const getFields = ({
  projectIdOptions,
  cidr,
  userName,
  configurations,
  rootVolumeSizeRange = { min: 32, max: 2048, default: 32 },
  title
}) => {
  const fields = {
    name: {
      label: 'Dataspace Name',
      placeholder: 'Combination of letters, numbers and -, starts with a letter.',
      rules: 'required|string|between:3,128|WorkspaceNameRule1|WorkspaceNameRule2',
    },
    description: {
      label: 'Description',
      placeholder: 'The description of this dataspace',
      rules: 'required|string|between:1,2048',
      value: `${userName}'s Dataspace`,
    },
    projectId: {
      label: 'Project ID',
      placeholder: 'The project ID to associate with this dataspace',
      value: projectIdOptions[0]?.value,
      rules: ['required', 'string', 'min:1', 'max:100'],
      extra: {
        options: projectIdOptions,
        // explain: 'The project ID you wish to charge usage to.',
      },
    },
    rootVolume: {
      label: 'C Drive/Root Volume Size (GB)',
      value: `${Math.max(rootVolumeSizeRange.min, rootVolumeSizeRange.default ?? 32)}`,
      rules: ['required', 'integer', `min:${rootVolumeSizeRange.min}`, `max:${rootVolumeSizeRange.max}`],
      extra: {
        explain: 'Adjust the amount of storage space available on your dataspace.',
      },
    },
    envTypeConfigId: {
      label: 'Configuration',
      placeholder: 'The configuration for the dataspace',
      extra: {
        explain: 'Select your hardware configuration.',
      },
      value: configurations && configurations.length ? configurations[0]?.id : undefined,
      rules: 'required',
    },
    enableHyperthreading: {
      label: 'Hyperthreading',
      extra: {
        explain: 'If disabled, this will half your number of vCPUs, where 1 vCPU will equal 1 physical core.',
      },
      value: true,
      rules: ['boolean', 'required'],
    },
    crossCommunication: {
      label: 'Allow dataspace cross-communication',
      extra: {
        explain: 'If enabled, this dataspace can communicate with other dataspaces within your project.'
      },
      value: true,
      rules: ['boolean', 'required'],
    },
    ...getExtraFields(title)
  };

  if (!_.isUndefined(cidr)) {
    fields.cidr = {
      label: 'Restricted CIDR',
      extra: {
        explain: `This is your IP address. Your dataspace will only be reachable from this CIDR (IP address range) and on campus. You can change your IP address whitelist after creating the dataspace.`,
      },
      placeholder: 'The CIDR range to restrict dataspace access to',
      rules: 'required|cidr',
      value: cidr,
    };
  }

  return fields;
};

// Keywords that trigger the testbed URL field to appear.
const repoFieldKeywords = ['testbed', 'ads', 'solution'];

function getExtraFields(title) {
  const extraFields = {};
  if (title) {
    const titleLower = title.toLowerCase();
    if (repoFieldKeywords.some(keyword => titleLower.includes(keyword))) {
      extraFields.IDSTestbedRepo = {
        label: 'Testbed URL',
        placeholder: 'URL of Testbed',
        value: `https://github.com/RACE-RMIT-Advanced-Cloud-Ecosystem/ADS-Testbed`,
      };
    }
  }

  return extraFields;
}

function getCreateInternalEnvForm(...args) {
  return createForm(getFields(...args));
}

// eslint-disable-next-line import/prefer-default-export
export { getCreateInternalEnvForm };
